// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

import { vueProps } from '@/vue-app'
import { LOGIN_STATUS } from '@/store/mutation-types'

const AUTH_CONFIG = {
  MAX_REDIRECTS: 3,
  DEBOUNCE_MANUAL: 1500,
  DEBOUNCE_ERROR: 8000,
  DEBOUNCE_TIMEOUT: 6000,
  LOCK_NORMAL: 2000,
  LOCK_ERROR: 12000,
  BLOCK_SAML: 10000,
  RATE_LIMIT_PAUSE: 20000,
  REDIRECT_DELAY: 200,
  REQUEST_TIMEOUT: 600000
}

/**
 * Serviço centralizado de autenticação com proteções anti-loop
 * Versão otimizada com tempos reduzidos mantendo robustez
 */
class AuthService {
  constructor () {
    this.store = null
    this.isInitialized = false
    this.redirectCount = 0
    this.logoutInProgress = false
    this.lastLogoutTime = 0
  }

  init (store) {
    if (this.isInitialized) {
      return
    }

    this.store = store
    this.isInitialized = true
    this.setupStorageListener()

    const samlDetected = this.detectSamlReturn()

    if (samlDetected) {
      this.setLoginStatus(1)
      this.cleanSamlUrlParams()
    }
  }

  setLoginStatus (status, sessionKey = null) {
    if (!this.isInitialized) {
      return
    }

    const numericStatus = parseInt(status)

    this.store.commit('SET_LOGIN_STATUS', numericStatus)

    if (numericStatus === 1) {
      vueProps.$localStorage.set(LOGIN_STATUS, numericStatus)
      window.localStorage.setItem('LOGIN_STATUS', '1')
      if (sessionKey) {
        window.localStorage.setItem('SESSION_KEY', sessionKey)
      }

      this.redirectCount = 0
      this.logoutInProgress = false
    } else {
      vueProps.$localStorage.remove(LOGIN_STATUS)
      window.localStorage.setItem('LOGIN_STATUS', '0')
      window.localStorage.removeItem('SESSION_KEY')
    }
  }

  isLoggedIn () {
    if (!this.isInitialized) {
      return false
    }

    const storeStatus = this.store?.getters?.loginStatus || 0
    const localStatus = parseInt(vueProps.$localStorage.get(LOGIN_STATUS, 0))
    const storageStatus = parseInt(window.localStorage.getItem('LOGIN_STATUS') || 0)

    if (storeStatus === localStatus && localStatus === storageStatus) {
      return storeStatus === 1
    }

    if (this.logoutInProgress) {
      return false
    }

    const correctedStatus = Math.max(storeStatus, localStatus, storageStatus)
    this.setLoginStatus(correctedStatus)

    return correctedStatus === 1
  }

  detectSamlReturn () {
    if (this.logoutInProgress) {
      return false
    }

    if (this._isInBlockingPeriod(AUTH_CONFIG.BLOCK_SAML)) {
      return false
    }

    const cookies = document.cookie
    const hasSessionKey = cookies.includes('sessionkey=')
    const hasSamlFlag = cookies.includes('isSAML=true')
    const referrer = document.referrer

    const isFromSaml = referrer.includes('sts.lwsa.tech') ||
                       referrer.includes('sts-hom.lwsa.tech') ||
                       referrer.includes('idp-redirect.lwsa.tech') ||
                       referrer.includes('idp-redirect-qa.lwsa.tech') ||
                       referrer.includes('login.kinghost.com.br')

    if (hasSamlFlag && isFromSaml) {
      return true
    }

    return hasSessionKey && (hasSamlFlag || isFromSaml)
  }

  async performLogout (reason = 'manual', shouldRedirect = false) {
    const now = Date.now()
    const debounceTime = this._getDebounceTime(reason)

    if ((now - this.lastLogoutTime) < debounceTime) {
      return Promise.resolve()
    }

    this.lastLogoutTime = now
    this.logoutInProgress = true

    try {
      this.setLoginStatus(0)
      this._clearCookies()

      if (this.store?.getters?.token) {
        this._performApiLogout().catch(() => {})
      }

      if (shouldRedirect) {
        setTimeout(() => {
          this._redirectToLogin()
        }, AUTH_CONFIG.REDIRECT_DELAY)
      }

      const lockTime = reason === 'manual' ? AUTH_CONFIG.LOCK_NORMAL : AUTH_CONFIG.LOCK_ERROR
      setTimeout(() => {
        this.logoutInProgress = false
      }, lockTime)
    } catch (error) {
      this.logoutInProgress = false
    }

    return Promise.resolve()
  }

  checkValidLogin () {
    if (this.logoutInProgress) return false
    if (this._isInBlockingPeriod(AUTH_CONFIG.LOCK_ERROR)) {
      return this.isLoggedIn()
    }

    const isLoggedIn = this.isLoggedIn()
    if (!isLoggedIn && this.detectSamlReturn()) {
      this.setLoginStatus(1)
      this.cleanSamlUrlParams()
      return true
    }

    return isLoggedIn
  }

  canRedirect () {
    this.redirectCount++
    if (this.redirectCount > AUTH_CONFIG.MAX_REDIRECTS) {
      return false
    }
    return true
  }

  cleanSamlUrlParams () {
    if (typeof window === 'undefined') return
    const url = new URL(window.location)
    if (url.searchParams.has('SAMLResponse') || url.searchParams.has('RelayState')) {
      url.searchParams.delete('SAMLResponse')
      url.searchParams.delete('RelayState')
      window.history.replaceState({}, document.title, url.toString())
    }
  }

  setupStorageListener () {
    if (typeof window === 'undefined') return

    window.addEventListener('storage', (event) => {
      if (event.key === 'LOGIN_STATUS' && event.newValue === '0') {
        const currentStatus = this.store?.getters?.loginStatus || 0
        if (currentStatus === 1) {
          this.logoutInProgress = true
          setTimeout(() => {
            this.performLogout('tab-sync', true)
          }, 300)
        }
      }
    })
  }

  forceReset () {
    this.logoutInProgress = false
    this.redirectCount = 0
    this.lastLogoutTime = 0
    this.setLoginStatus(0)
  }

  _getDebounceTime (reason) {
    switch (reason) {
      case 'timeout':
      case 'error':
      case 'rate-limit':
        return AUTH_CONFIG.DEBOUNCE_ERROR
      case 'tab-sync':
        return AUTH_CONFIG.DEBOUNCE_MANUAL
      default:
        return AUTH_CONFIG.DEBOUNCE_MANUAL
    }
  }

  _isInBlockingPeriod (blockTime = AUTH_CONFIG.BLOCK_SAML) {
    return (Date.now() - this.lastLogoutTime) < blockTime
  }

  _clearCookies () {
    const cookies = ['sessionkey', 'isSAML', 'username', 'account', 'domainid', 'role', 'timezone', 'timezoneoffset']
    cookies.forEach(name => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    })
  }

  async _performApiLogout () {
    try {
      const token = this.store?.getters?.token
      if (!token) return

      const { axios } = await import('@/customizations/utils/request')
      await axios.get('/client/api', {
        params: {
          command: 'logout',
          sessionkey: token,
          response: 'json'
        }
      })
    } catch (error) {
      // Silent error
    }
  }

  _redirectToLogin () {
    if (typeof window === 'undefined') return

    const currentPath = window.location.hash.replace('#', '')
    if (currentPath !== '/user/login') {
      window.location.hash = '#/user/login'
    }
  }
}

const authService = new AuthService()

export default authService
export { AUTH_CONFIG }
